import type { SavedView } from "../../domain/app-types.ts";
import type { SortDirection, TaskSort } from "../../domain/entities.ts";
import type { AppView } from "../../app/shell-view.ts";
import type { DestinationRef } from "./destinations.ts";

export interface NavigationWorkflowState {
  filter: AppView;
  activeRoot: string | null;
  categoryFilter: string | null;
}

export interface NavigationWorkflowCapabilities {
  state(): NavigationWorkflowState;
  setViewState(update: {
    filter?: AppView;
    activeRoot?: string | null;
    categoryFilter?: string | null;
    activeSavedView?: string | null;
    groupBy?: "none";
    viewSort?: TaskSort;
    viewSortDirection?: SortDirection;
    viewPageSize?: number;
    taskPage?: number;
    expandedSpaceId?: string | null;
    openSidebarMenu?: null;
  }): void;
  clearTaskPanel(): void;
  clearTaskDraft(): void;
  closeMobileNavigation(): void;
  confirmDiscard(): Promise<boolean>;
  savedView(id: string): SavedView | undefined;
  defaultSavedView(rootId: string): SavedView | undefined;
  activateSavedView(view: SavedView): void;
  rememberVisited(destination: DestinationRef): Promise<void>;
  restoreTaskListMode(): Promise<void>;
  persistMeta(key: string, value: unknown): Promise<void>;
  render(): Promise<void>;
  refreshCalendar(): Promise<void>;
  setSearch(value: string): void;
  rememberSearch(): void | Promise<void>;
  setInactiveDays(value: string): void | Promise<void>;
  measureSearch(startedAt: number): void;
}

export function createNavigationWorkflow(capabilities: NavigationWorkflowCapabilities) {
  const resetCustomView = () => capabilities.setViewState({ filter: "open", groupBy: "none", viewSort: "manual", viewSortDirection: "asc", viewPageSize: 0 });

  return {
    async selectView(view: AppView): Promise<void> {
      if (!await capabilities.confirmDiscard()) return;
      capabilities.setViewState({
        filter: view,
        ...(view === "myday" || view === "dashboard" ? { categoryFilter: null } : {}),
        activeSavedView: null,
        groupBy: "none",
        viewSort: "manual",
        viewSortDirection: "asc",
        viewPageSize: 0,
        taskPage: 1,
        openSidebarMenu: null,
      });
      capabilities.clearTaskPanel();
      capabilities.closeMobileNavigation();
      if (view === "calendar") await capabilities.refreshCalendar();
      else await capabilities.render();
    },

    async selectSavedView(id: string): Promise<void> {
      if (!await capabilities.confirmDiscard()) return;
      const view = capabilities.savedView(id);
      if (!view) return;
      if (view.rootId) {
        const switchedRoot = view.rootId !== capabilities.state().activeRoot;
        capabilities.setViewState({ activeRoot: view.rootId, ...(switchedRoot ? { categoryFilter: null } : {}) });
        await capabilities.persistMeta("activeRoot", view.rootId);
        if (switchedRoot) await capabilities.persistMeta("activeCollection", null);
      }
      capabilities.activateSavedView(view);
      await capabilities.rememberVisited({ kind: "saved-view", id: view.id, rootId: view.rootId });
      capabilities.clearTaskPanel();
      capabilities.closeMobileNavigation();
      await capabilities.render();
    },

    async selectRoot(id: string, options: { showAllTasks?: boolean } = {}): Promise<void> {
      if (!await capabilities.confirmDiscard()) return;
      if (options.showAllTasks) { capabilities.setSearch(""); resetCustomView(); }
      capabilities.setViewState({ activeRoot: id, expandedSpaceId: id, categoryFilter: null, activeSavedView: null, openSidebarMenu: null, taskPage: 1 });
      await capabilities.rememberVisited({ kind: "space", id });
      await capabilities.restoreTaskListMode();
      capabilities.clearTaskDraft();
      const defaultView = options.showAllTasks ? undefined : capabilities.defaultSavedView(id);
      if (defaultView) capabilities.activateSavedView(defaultView);
      else if (capabilities.state().filter === "custom") resetCustomView();
      await capabilities.persistMeta("activeRoot", id);
      await capabilities.persistMeta("activeCollection", null);
      await capabilities.persistMeta("expandedSpaceId", id);
      capabilities.clearTaskPanel();
      capabilities.closeMobileNavigation();
      await capabilities.render();
    },

    async selectCategory(id: string, rootId: string, options: { clearSearch?: boolean } = {}): Promise<void> {
      if (!await capabilities.confirmDiscard()) return;
      if (options.clearSearch) capabilities.setSearch("");
      capabilities.setViewState({ activeRoot: rootId, categoryFilter: id, filter: "open", groupBy: "none", expandedSpaceId: rootId, activeSavedView: null, openSidebarMenu: null, taskPage: 1 });
      await capabilities.rememberVisited({ kind: "collection", id, rootId });
      await capabilities.restoreTaskListMode();
      capabilities.clearTaskDraft();
      if (capabilities.state().filter === "custom") resetCustomView();
      await capabilities.persistMeta("activeRoot", rootId);
      await capabilities.persistMeta("activeCollection", { id, rootId });
      await capabilities.persistMeta("expandedSpaceId", rootId);
      capabilities.clearTaskPanel();
      capabilities.closeMobileNavigation();
      await capabilities.render();
    },

    async search(value: string): Promise<void> {
      const startedAt = performance.now();
      capabilities.setSearch(value);
      capabilities.setViewState({ taskPage: 1 });
      await capabilities.render();
      capabilities.measureSearch(startedAt);
    },

    rememberSearch: capabilities.rememberSearch,

    async setInactiveDays(value: string): Promise<void> {
      await capabilities.setInactiveDays(value);
      await capabilities.render();
    },
  };
}
